import { useState, useCallback, useRef } from "react";
import {
  fetchDownloadFiles,
  downloadAllFilesParallel,
  downloadSingleFile,
  isFileSystemAccessSupported,
} from "../../../services/s3DownloadService";
import { deleteJobFiles } from "../../../services/deleteFilesService";
import { DOWNLOAD_STATUS, FILE_STATUS } from "../../../constants";

export { DOWNLOAD_STATUS, FILE_STATUS };

export function useDownload(videoFileName, jobId) {
  const [downloadStatus, setDownloadStatus] = useState(DOWNLOAD_STATUS.READY);
  const [downloadError, setDownloadError] = useState("");
  // fileList: [{ filename, url, status, error }]
  const [fileList, setFileList] = useState([]);
  const [summary, setSummary] = useState(null); // { succeeded, failed }
  // Folder chosen via File System Access API (Chromium). null => legacy blob/anchor download.
  const dirHandleRef = useRef(null);

  const updateFileStatus = useCallback((filename, status, error = "") => {
    setFileList((prev) => prev.map((f) => (f.filename === filename ? { ...f, status, error } : f)));
  }, []);

  const retryFile = useCallback(
    async (filename) => {
      // Prevent retry if another download is already in progress
      if (
        downloadStatus === DOWNLOAD_STATUS.FETCHING ||
        downloadStatus === DOWNLOAD_STATUS.DOWNLOADING
      )
        return;

      await downloadSingleFile(
        filename,
        videoFileName,
        jobId,
        updateFileStatus,
        dirHandleRef.current
      );

      // Recalculate summary after retry
      setFileList((prev) => {
        const succeeded = prev.filter((f) => f.status === FILE_STATUS.DONE).length;
        const failed = prev.filter((f) => f.status === FILE_STATUS.ERROR).length;
        setSummary({ succeeded, failed });

        // If all files are now done — upgrade overall status to DOWNLOADED
        // and trigger S3 cleanup for the completed job.
        if (failed === 0 && succeeded === prev.length) {
          setDownloadStatus(DOWNLOAD_STATUS.DOWNLOADED);
          // eslint-disable-next-line no-console
          console.log("[useDownload] All retried files done — triggering S3 cleanup.");
          deleteJobFiles(jobId).catch((err) =>
            // eslint-disable-next-line no-console
            console.warn("[useDownload] ⚠️ delete_files failed (non-blocking):", err.message)
          );
        }
        return prev;
      });
    },
    [downloadStatus, videoFileName, jobId, updateFileStatus]
  );

  const startDownload = useCallback(async () => {
    // Ask for a target folder FIRST (needs the user's click gesture, so it must come before
    // any other await). If unsupported or cancelled, fall back to the legacy download path.
    dirHandleRef.current = null;
    if (isFileSystemAccessSupported()) {
      try {
        // startIn: "downloads" opens the picker directly inside the OS Downloads folder
        // (avoids Chrome defaulting to a restricted/system location, which previously
        // triggered "can't open this folder because it contains system files").
        dirHandleRef.current = await window.showDirectoryPicker({
          mode: "readwrite",
          startIn: "downloads",
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[useDownload] Folder picker skipped, using standard download:", err?.message);
      }
    }

    setDownloadError("");
    setSummary(null);
    setFileList([]);

    setDownloadStatus(DOWNLOAD_STATUS.FETCHING);

    let files;
    try {
      if (!jobId) throw new Error("Job ID is missing. Cannot process download.");
      files = await fetchDownloadFiles(videoFileName, jobId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[useDownload] ❌ Fetch failed:", err.message);
      setDownloadError(err.message || "Failed to fetch download files. Please try again.");
      setDownloadStatus(DOWNLOAD_STATUS.ERROR);
      return;
    }

    setFileList(
      files.map((f) => ({
        filename: f.filename,
        url: f.url,
        status: FILE_STATUS.PENDING,
        error: "",
      }))
    );

    setDownloadStatus(DOWNLOAD_STATUS.DOWNLOADING);

    const result = await downloadAllFilesParallel(files, updateFileStatus, dirHandleRef.current);
    setSummary(result);

    if (result.failed === 0) {
      // eslint-disable-next-line no-console
      console.log("[useDownload] ✅ All files downloaded successfully.");
      setDownloadStatus(DOWNLOAD_STATUS.DOWNLOADED);

      // eslint-disable-next-line no-console
      console.log("[useDownload] Triggering S3 cleanup — jobId:", jobId);
      deleteJobFiles(jobId).catch((err) =>
        // eslint-disable-next-line no-console
        console.warn("[useDownload] ⚠️ delete_files failed (non-blocking):", err.message)
      );
    } else if (result.succeeded === 0) {
      // Every single file failed — treat as overall error
      setDownloadError(`All ${result.failed} file(s) failed to download. Please retry.`);
      setDownloadStatus(DOWNLOAD_STATUS.ERROR);
    } else {
      // Partial success — mark overall as downloaded so UI shows the list
      // eslint-disable-next-line no-console
      console.warn(`[useDownload] ⚠️ Partial: ${result.succeeded} ok, ${result.failed} failed.`);
      setDownloadStatus(DOWNLOAD_STATUS.DOWNLOADED);
    }
  }, [videoFileName, jobId, updateFileStatus]);

  const isFetching = downloadStatus === DOWNLOAD_STATUS.FETCHING;
  const isDownloading = downloadStatus === DOWNLOAD_STATUS.DOWNLOADING;
  const isBusy = isFetching || isDownloading;
  const isDownloaded = downloadStatus === DOWNLOAD_STATUS.DOWNLOADED;
  const isError = downloadStatus === DOWNLOAD_STATUS.ERROR;
  const hasPartialFailure = isDownloaded && summary?.failed > 0;

  return {
    downloadStatus,
    downloadError,
    fileList,
    summary,
    startDownload,
    retryFile,
    isFetching,
    isDownloading,
    isBusy,
    isDownloaded,
    isError,
    hasPartialFailure,
  };
}
